/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

 * Angepasst für LOOPBOX Portal:
 * Einfaches Dashboard mit Einstiegskarten für Bildschirme,
 * Medien und Rechnungen.
=========================================================
*/

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { apiFetch } from "api/client";

const PROFILE_KEY = "solvit_profile";
const TOKEN_KEY = "solvit_auth_token";

function OverviewCard({
  icon,
  value,
  label,
  cardColor,
  description,
  buttonLabel,
  onClick,
  detail,
}) {
  return (
    <Card
      sx={{
        borderRadius: "0.7rem",
        border: "1px solid #d4deea",
        boxShadow: "none",
        overflow: "hidden",
        height: "100%",
      }}
    >
      <MDBox
        sx={{
          display: "grid",
          gridTemplateColumns: "76px 1fr",
          alignItems: "stretch",
          background: cardColor,
          borderBottom: "1px solid rgba(255,255,255,0.35)",
        }}
      >
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ borderRight: "1px solid rgba(255,255,255,0.35)" }}
        >
          <Icon sx={{ color: "white", fontSize: "1.65rem !important" }}>{icon}</Icon>
        </MDBox>
        <MDBox px={2} py={1.35}>
          <MDTypography variant="h4" color="white" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
            {value}
          </MDTypography>
          <MDTypography variant="button" color="white" sx={{ opacity: 0.94 }}>
            {label}
          </MDTypography>
        </MDBox>
      </MDBox>
      <MDBox p={2.2}>
        <MDTypography variant="button" color="text" display="block" mb={0.6}>
          {description}
        </MDTypography>
        {detail && (
          <MDTypography variant="caption" color="text" display="block" mb={1.2}>
            {detail}
          </MDTypography>
        )}
        <MDButton variant="gradient" color="info" fullWidth onClick={onClick}>
          {buttonLabel}
        </MDButton>
      </MDBox>
    </Card>
  );
}

function Dashboard() {
  const navigate = useNavigate();

  const [screenCount, setScreenCount] = useState(null);
  const [mediaCount, setMediaCount] = useState(null);
  const [latestInvoice, setLatestInvoice] = useState(null);
  const [profile, setProfile] = useState(null);
  const [ticketText, setTicketText] = useState("");
  const [ticketSending, setTicketSending] = useState(false);
  const [ticketInfo, setTicketInfo] = useState("");

  const loadStats = useCallback(async () => {
    const safeParse = (raw) => {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };

    // Profile (still from localStorage for now)
    const profileRaw = localStorage.getItem(PROFILE_KEY);
    const p = profileRaw ? safeParse(profileRaw) : null;
    setProfile(p);

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setScreenCount(0);
      setMediaCount(0);
      setLatestInvoice(null);
      return;
    }

    try {
      // Screens
      const screens = await apiFetch("/screens", { token });
      setScreenCount(Array.isArray(screens) ? screens.length : 0);

      // Media
      const media = await apiFetch("/media", { token });
      setMediaCount(Array.isArray(media) ? media.length : 0);

      // Invoices (latest)
      const invoices = await apiFetch("/invoices", { token });
      if (Array.isArray(invoices) && invoices.length > 0) {
        const sorted = [...invoices].sort((a, b) => {
          const da = new Date(a.issuedAt || a.createdAt || 0).getTime();
          const db = new Date(b.issuedAt || b.createdAt || 0).getTime();
          return db - da;
        });
        setLatestInvoice(sorted[0]);
      } else {
        setLatestInvoice(null);
      }
    } catch (e) {
      console.error("Fehler beim Laden der Dashboard-Daten", e);
      // Fallbacks (do not crash UI)
      setScreenCount(0);
      setMediaCount(0);
      setLatestInvoice(null);
    }
  }, []);

  useEffect(() => {
    void loadStats();

    const onFocus = () => void loadStats();
    const onVisibility = () => {
      if (document.visibilityState === "visible") void loadStats();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [loadStats]);

  const displayLatestInvoice = latestInvoice
    ? `${latestInvoice.month || "Letzte Rechnung"} • ${latestInvoice.amount || ""}`
    : "Noch keine Rechnungen";
  const contactName = profile?.contactName || "Ansprechpartner";
  const screenValue = screenCount === null ? "–" : String(screenCount);
  const mediaValue = mediaCount === null ? "–" : String(mediaCount);
  const invoiceValue = latestInvoice ? "1" : "0";

  const handleCreateTicket = async () => {
    const text = ticketText.trim();
    if (!text) return;

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setTicketInfo("Du bist nicht eingeloggt. Bitte logge dich neu ein.");
      return;
    }

    setTicketSending(true);
    setTicketInfo("");
    try {
      await apiFetch("/messages", {
        token,
        method: "POST",
        body: JSON.stringify({
          text: `[Ticket] ${text}`,
        }),
      });
      setTicketText("");
      setTicketInfo("Ticket wurde an den Support gesendet.");
    } catch (e) {
      setTicketInfo(e?.message || "Ticket konnte nicht gesendet werden.");
    } finally {
      setTicketSending(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Begrüßung */}
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="medium" mb={0.5}>
            {`Hey, ${contactName}!`}
          </MDTypography>
          <MDTypography variant="button" color="text">
            Hier verwaltest du deine Bildschirme, Medien und Rechnungen.
          </MDTypography>
          {profile?.role === "admin" && (
            <MDTypography variant="caption" color="text" display="block" mt={1}>
              Admin-Modus: Statistiken zeigen alle Kunden.
            </MDTypography>
          )}
        </MDBox>

        {/* Hauptkarten */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <OverviewCard
              icon="tv"
              value={screenValue}
              label="Aktive Bildschirme"
              cardColor="linear-gradient(135deg, #0d8df6 0%, #077be2 100%)"
              description="Verwalte Bildschirme und aktualisiere ihre Loops."
              buttonLabel="Bildschirme öffnen"
              onClick={() => navigate("/screens")}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <OverviewCard
              icon="perm_media"
              value={mediaValue}
              label="Medien gesamt"
              cardColor="linear-gradient(135deg, #ff6a7b 0%, #ef5b56 100%)"
              description="Lade Bilder und Videos hoch und halte die Mediathek aktuell."
              buttonLabel="Mediathek öffnen"
              onClick={() => navigate("/media")}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <OverviewCard
              icon="receipt_long"
              value={invoiceValue}
              label="Offene Rechnung"
              cardColor="linear-gradient(135deg, #ffc96b 0%, #f0b14a 100%)"
              description="Sieh dir neue oder offene Rechnungen sofort an."
              detail={displayLatestInvoice}
              buttonLabel="Rechnungen öffnen"
              onClick={() => navigate("/invoices")}
            />
          </Grid>
        </Grid>

        {/* Ticket-Block */}
        <MDBox mt={4}>
          <Card
            sx={{
              borderRadius: "0.7rem",
              border: "1px solid #d4deea",
              boxShadow: "none",
            }}
          >
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={1}>
                Ticket erstellen
              </MDTypography>
              <MDTypography variant="body2" color="text" mb={1.5}>
                Wenn etwas nicht funktioniert, schreib kurz dein Problem. Das Ticket wird direkt als Messenger-Nachricht an den Support gesendet.
              </MDTypography>
              <MDBox display="flex" gap={1} flexDirection={{ xs: "column", md: "row" }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Beschreibe dein Problem..."
                  value={ticketText}
                  onChange={(e) => setTicketText(e.target.value)}
                  disabled={ticketSending}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void handleCreateTicket();
                    }
                  }}
                />
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={() => {
                    void handleCreateTicket();
                  }}
                  disabled={ticketSending || !ticketText.trim()}
                >
                  {ticketSending ? "Sende..." : "Ticket senden"}
                </MDButton>
              </MDBox>
              {ticketInfo && (
                <MDTypography variant="caption" color="text" mt={1} display="block">
                  {ticketInfo}
                </MDTypography>
              )}
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
